/**
 * Shared courses: the list, the publish flow, and the analytics for one course.
 *
 * ── The thing this screen has to be honest about ────────────────────────────
 * Publishing takes a copy of a course **out of the vault**. Everywhere else in
 * Cloud, "Mauvely cannot read this" is true without qualification; here it stops
 * being true for the copy, because a learner with no account and no key has to
 * be able to open it. That is said plainly before anyone clicks, not in a
 * footnote afterwards, and it is why the button says "Publish" rather than
 * "Share".
 *
 * The tab is shown to everyone and greyed with a diamond below Max, following
 * the same pattern as the audit log: the page behind it is what sells the tier,
 * and hiding it means nobody finds out the feature exists.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, errorText, type CourseWire, type CourseAnalyticsWire } from '../api';
import { ds } from '../ds';
import { useApp } from '../state';
import { entitlements, isUnlimited } from '@shared/entitlements';
import { PageTitle, Panel } from '../components/Shell';
import { SkeletonRows } from '../components/Skeleton';
import { courseUrl, publishCourse, type PublishProgress } from '../courses';
import { getCachedKey, listFolder, type DecryptedNode } from '../nodes';
import { accountOrigin } from './Vault';
import { formatBytes, formatWhen, monoPlain } from '../theme';
import { useT } from '../i18n';

/** A caution the reader must not skim. Louder than ShareDialog's, deliberately:
 *  this one is the moment a file leaves the vault. */
const Warning = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      padding: '12px 14px',
      borderRadius: 10,
      background: 'var(--warn-100, rgba(214,158,46,.12))',
      border: '1px solid var(--warn-300, rgba(214,158,46,.35))',
      fontSize: 13.5,
      lineHeight: 1.55,
      color: 'var(--text-body)'
    }}
  >
    {children}
  </div>
);

export function Courses() {
  const t = useT();
  const { Button } = ds();
  const { me } = useApp();

  const [courses, setCourses] = useState<CourseWire[]>([]);
  const [limit, setLimit] = useState(0);
  const [live, setLive] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [progress, setProgress] = useState<PublishProgress | null>(null);
  const [viewing, setViewing] = useState<string | null>(null);

  const allowed = entitlements(me?.plan).publishedCourses !== 0;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.courses.list();
      setCourses(res.courses);
      setLimit(res.limit);
      setLive(res.live);
      setError(null);
    } catch (err) {
      setError(errorText(err, 'Could not load your courses.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (allowed) void load();
    else setLoading(false);
  }, [allowed, load]);

  if (!allowed) return <UpgradeWall />;
  if (viewing) return <Analytics id={viewing} onBack={() => { setViewing(null); void load(); }} />;

  const atCap = !isUnlimited(limit) && live >= limit;

  return (
    <div style={{ display: 'grid', gap: 22 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap'
        }}
      >
        <PageTitle
          title={t('courses.shared-courses')}
          meta={
            isUnlimited(limit)
              ? `${live} live`
              : `${live} of ${limit} live`
          }
        />
        <Button
          variant="primary"
          size="md"
          disabled={atCap || !!progress}
          onClick={() => setPicking(true)}
        >
          {t('courses.share-as-course')}
        </Button>
      </div>

      {atCap && (
        <Warning>
          You have {limit} courses live. Unpublish one to free a slot.
        </Warning>
      )}

      {error && (
        <Panel style={{ padding: '14px 20px', background: 'var(--danger-100)' }}>
          <span style={{ fontSize: 13.5, color: 'var(--danger-600)' }}>{error}</span>
        </Panel>
      )}

      {progress && <PublishProgressPanel progress={progress} />}

      {picking && (
        <PackagePicker
          onCancel={() => setPicking(false)}
          onPublish={async (node, key, title) => {
            setPicking(false);
            setError(null);
            try {
              await publishCourse(node, key, title, setProgress);
              await load();
            } catch (err) {
              setError(errorText(err, 'Could not publish that course.'));
            } finally {
              setProgress(null);
            }
          }}
        />
      )}

      <Panel style={{ paddingTop: 6, paddingBottom: 6 }}>
        {loading ? (
          <SkeletonRows rows={3} label={t('courses.loading-your-courses')} />
        ) : courses.length === 0 ? (
          <div style={{ padding: '34px 22px', display: 'grid', gap: 8 }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-strong)' }}>
              {t('courses.nothing-shared-yet')}
            </span>
            <span style={monoPlain}>
              {t('courses.export-a-course-from-compose')}
            </span>
          </div>
        ) : (
          courses.map((c, i) => (
            <CourseRow
              key={c.id}
              course={c}
              last={i === courses.length - 1}
              onChanged={load}
              onAnalytics={() => setViewing(c.id)}
            />
          ))
        )}
      </Panel>

    </div>
  );
}

function CourseRow({
  course,
  last,
  onChanged,
  onAnalytics
}: {
  course: CourseWire;
  last: boolean;
  onChanged: () => Promise<void>;
  onAnalytics: () => void;
}) {
  const t = useT();
  const { Button, Icon } = ds();
  const [busy, setBusy] = useState(false);
  const url = courseUrl(course.slug);

  const toggle = async () => {
    setBusy(true);
    try {
      if (course.state === 'live') await api.courses.unpublish(course.id);
      else await api.courses.publish(course.id);
      await onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '14px 22px',
        borderBottom: last ? 'none' : '1px solid var(--border-subtle)',
        flexWrap: 'wrap'
      }}
    >
      <span
        style={{
          width: 32,
          height: 32,
          borderRadius: 9,
          display: 'grid',
          placeItems: 'center',
          background: course.state === 'live' ? 'var(--accent-100)' : 'var(--surface-sunken)',
          color: course.state === 'live' ? 'var(--accent-600)' : 'var(--text-muted)',
          flex: '0 0 auto'
        }}
      >
        <Icon name="graduation-cap" size={17} />
      </span>

      <div style={{ flex: 1, minWidth: 180, display: 'grid', gap: 3 }}>
        <span style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--text-strong)' }}>
          {course.title}
        </span>
        <span style={monoPlain}>
          {course.state === 'live' ? (
            <a href={url} target="_blank" rel="noreferrer" style={{ color: 'var(--text-link)' }}>
              {url}
            </a>
          ) : (
            'Not live'
          )}
        </span>
      </div>

      <span style={{ ...monoPlain, minWidth: 140 }}>
        {course.learners} learner{course.learners === 1 ? '' : 's'} ·{' '}
        {course.completions} completed
      </span>

      <div style={{ display: 'flex', gap: 8 }}>
        <Button variant="ghost" size="sm" onClick={onAnalytics}>
          {t('courses.report')}
        </Button>
        <Button variant="ghost" size="sm" disabled={busy} onClick={() => void toggle()}>
          {course.state === 'live' ? 'Unpublish' : 'Publish'}
        </Button>
      </div>
    </div>
  );
}

function PublishProgressPanel({ progress }: { progress: PublishProgress }) {
  const label = {
    downloading: 'Downloading and decrypting the package',
    unpacking: 'Unpacking',
    uploading: 'Uploading',
    publishing: 'Publishing'
  }[progress.phase];

  return (
    <Panel style={{ padding: '18px 22px', display: 'grid', gap: 10 }}>
      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-strong)' }}>
        {label}… {progress.percent}%
      </span>
      <div style={{ height: 6, borderRadius: 999, background: 'var(--surface-sunken)' }}>
        <div
          style={{
            height: '100%',
            width: `${progress.percent}%`,
            borderRadius: 999,
            background: 'var(--accent-600)',
            transition: 'width .2s ease'
          }}
        />
      </div>
      {progress.detail && <span style={monoPlain}>{progress.detail}</span>}
    </Panel>
  );
}

/**
 * Pick a `.zip` already in the vault.
 *
 * Deliberately not a file upload: the package should be in Cloud anyway, and
 * publishing something that was never stored would leave a live course with no
 * source to republish from.
 */
function PackagePicker({
  onCancel,
  onPublish
}: {
  onCancel: () => void;
  onPublish: (node: DecryptedNode, key: CryptoKey, title: string) => void | Promise<void>;
}) {
  const t = useT();
  const { Button, Input } = ds();
  const { me } = useApp();
  const rootId = me?.rootNodeId ?? null;
  const [nodes, setNodes] = useState<DecryptedNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [chosen, setChosen] = useState<DecryptedNode | null>(null);
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        // Listing the root also decrypts and caches every child's key, which is
        // what makes `getCachedKey` below a lookup rather than a second fetch.
        if (!rootId) throw new Error('Your vault is not open.');
        const contents = await listFolder(rootId);
        setNodes(
          contents.nodes.filter(
            (n) => n.kind === 'file' && /\.zip$/i.test(n.name) && !n.trashedAt
          )
        );
      } catch (err) {
        setError(errorText(err, 'Could not list your files.'));
      } finally {
        setLoading(false);
      }
    })();
  }, [rootId]);

  return (
    <Panel style={{ padding: '22px 26px', display: 'grid', gap: 16 }}>
      <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-strong)' }}>
        {t('courses.which-package')}
      </span>

      <Warning>
        {t('courses.publishing-puts-a-readable-copy')}
      </Warning>

      {error && <span style={{ fontSize: 13, color: 'var(--danger-600)' }}>{error}</span>}

      {loading ? (
        <SkeletonRows rows={3} label={t('courses.looking-for-course-packages')} />
      ) : nodes.length === 0 ? (
        <span style={monoPlain}>
          {t('courses.no-zip-files-in-your')}
        </span>
      ) : (
        <div style={{ display: 'grid', gap: 6 }}>
          {nodes.map((n) => (
            <button
              key={n.id}
              type="button"
              onClick={() => {
                setChosen(n);
                setTitle(n.name.replace(/\.zip$/i, ''));
              }}
              style={{
                textAlign: 'left',
                padding: '10px 14px',
                borderRadius: 10,
                cursor: 'pointer',
                border: `1px solid ${chosen?.id === n.id ? 'var(--accent-600)' : 'var(--border-subtle)'}`,
                background: chosen?.id === n.id ? 'var(--accent-100)' : 'transparent',
                color: 'var(--text-body)'
              }}
            >
              {n.name} <span style={monoPlain}>{formatBytes(n.plainSizeBytes)}</span>
            </button>
          ))}
        </div>
      )}

      {chosen && (
        <Input
          label={t('courses.course-title')}
          value={title}
          onChange={(e) => setTitle((e.target as HTMLInputElement).value)}
        />
      )}

      <div style={{ display: 'flex', gap: 10 }}>
        <Button
          variant="primary"
          size="md"
          disabled={!chosen || !title.trim()}
          onClick={async () => {
            if (!chosen) return;
            // Cached by the `listFolder` above, which decrypts every child's key
            // on the way past. If it is somehow absent the vault is locked, and
            // saying so beats a confusing failure three steps later.
            const key = getCachedKey(chosen.id);
            if (!key) {
              setError('Your vault is locked. Unlock it and try again.');
              return;
            }
            await onPublish(chosen, key, title.trim());
          }}
        >
          {t('courses.publish')}
        </Button>
        <Button variant="ghost" size="md" onClick={onCancel}>
          {t('courses.cancel')}
        </Button>
      </div>
    </Panel>
  );
}

/** One course's report. Visible to the publisher; never aggregated to Mauvely. */
function Analytics({ id, onBack }: { id: string; onBack: () => void }) {
  const t = useT();
  const { Button } = ds();
  const [data, setData] = useState<CourseAnalyticsWire | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setData(await api.courses.analytics(id));
      } catch (err) {
        setError(errorText(err, 'Could not load that report.'));
      }
    })();
  }, [id]);

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Button variant="ghost" size="sm" onClick={onBack}>
          {t('courses.all-courses')}
        </Button>
      </div>

      <PageTitle title={data ? `${data.course.title}.` : 'Report.'} meta="Completion and progress" />

      {error && (
        <Panel style={{ padding: '14px 20px', background: 'var(--danger-100)' }}>
          <span style={{ fontSize: 13.5, color: 'var(--danger-600)' }}>{error}</span>
        </Panel>
      )}

      {!data ? (
        <Panel style={{ paddingTop: 6, paddingBottom: 6 }}>
          <SkeletonRows rows={5} label={t('courses.loading-the-report')} />
        </Panel>
      ) : (
        <>
          <div
            style={{
              display: 'grid',
              gap: 14,
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))'
            }}
          >
            <Stat label={t('courses.learners')} value={String(data.summary.learners)} />
            <Stat label={t('courses.completed')} value={String(data.summary.completions)} />
            <Stat
              label={t('courses.average-score')}
              value={data.summary.averageScore === null ? '—' : `${data.summary.averageScore}%`}
            />
            <Stat
              label={t('courses.average-time')}
              value={
                data.summary.averageSeconds === null
                  ? '—'
                  : `${Math.round(data.summary.averageSeconds / 60)} min`
              }
            />
          </div>

          <Panel style={{ paddingTop: 6, paddingBottom: 6 }}>
            {data.learners.length === 0 ? (
              <div style={{ padding: '30px 22px' }}>
                <span style={monoPlain}>{t('courses.nobody-has-opened-this-course')}</span>
              </div>
            ) : (
              data.learners.map((l, i) => (
                <div
                  key={l.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    padding: '12px 22px',
                    borderBottom:
                      i === data.learners.length - 1 ? 'none' : '1px solid var(--border-subtle)',
                    flexWrap: 'wrap'
                  }}
                >
                  <span style={{ flex: 1, minWidth: 150, fontSize: 14 }}>
                    {l.name ?? <span style={monoPlain}>Anonymous learner</span>}
                    {l.email && <span style={{ ...monoPlain, marginLeft: 8 }}>{l.email}</span>}
                  </span>
                  <span style={{ ...monoPlain, minWidth: 90 }}>{l.percent}% done</span>
                  <span style={{ ...monoPlain, minWidth: 70 }}>
                    {l.score === null ? '—' : `${l.score}%`}
                  </span>
                  <span style={monoPlain}>
                    {l.completedAt ? `completed ${formatWhen(l.completedAt)}` : formatWhen(l.lastSeenAt)}
                  </span>
                </div>
              ))
            )}
          </Panel>

        </>
      )}
    </div>
  );
}

const Stat = ({ label, value }: { label: string; value: string }) => (
  <Panel style={{ padding: '16px 20px', display: 'grid', gap: 4 }}>
    <span style={monoPlain}>{label}</span>
    <span
      style={{
        fontFamily: "var(--font-display), 'Baloo 2', system-ui",
        fontWeight: 800,
        fontSize: 26,
        color: 'var(--text-strong)'
      }}
    >
      {value}
    </span>
  </Panel>
);

function UpgradeWall() {
  const t = useT();
  const { Button, Icon } = ds();

  return (
    <div style={{ display: 'grid', gap: 24 }}>
      <PageTitle title={t('courses.shared-courses')} meta="Part of Max" />
      <Panel style={{ padding: '34px 30px', display: 'grid', gap: 18, maxWidth: 640 }}>
        <span
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            display: 'grid',
            placeItems: 'center',
            background: 'var(--accent-100)',
            color: 'var(--accent-600)'
          }}
        >
          <Icon name="graduation-cap" size={22} />
        </span>

        <div style={{ display: 'grid', gap: 8 }}>
          <h2
            style={{
              fontFamily: "var(--font-display), 'Baloo 2', system-ui",
              fontWeight: 800,
              fontSize: 24,
              margin: 0,
              color: 'var(--text-strong)'
            }}
          >
            {t('courses.send-a-course-as-a')}
          </h2>
          <span style={{ fontSize: 14.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
            {t('courses.upload-a-package-from-compose')}
          </span>
        </div>

        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 9 }}>
          {[
            '15 courses live at once on Max, unlimited on Enterprise',
            'Who finished, how far everyone else got, and their score',
            'Restrict a course to named people on Enterprise'
          ].map((line) => (
            <li
              key={line}
              style={{ display: 'flex', gap: 9, fontSize: 14, color: 'var(--text-body)' }}
            >
              <span style={{ color: 'var(--accent-600)' }}>·</span>
              {line}
            </li>
          ))}
        </ul>

        <div>
          <Button
            variant="primary"
            size="md"
            onClick={() => {
              window.location.href = `${accountOrigin()}/pricing`;
            }}
          >
            {t('courses.see-max')}
          </Button>
        </div>
      </Panel>
    </div>
  );
}
